import type { CountryCode } from '../payment-gateways/core/types';

/**
 * Which hostname belongs to which country, and which country strings the
 * booking app is allowed to send. Both are deployment facts, not logic —
 * they change when a domain is registered or a new market opens, so they
 * live here rather than inside the resolver that reads them
 * (src/country/countryConfig.ts).
 *
 * Adding a country means editing this file and
 * src/constants/gateways.ts — plus `CountryCode` in
 * src/payment-gateways/core/types.ts, which is what makes TypeScript
 * point at anything still unhandled.
 */

/**
 * Hostname is the source of truth for country, since each market is a
 * separate deployment on its own domain. Suffix-matched, so `uat.`,
 * `staging.` and `www.` subdomains all resolve the same way.
 *
 * TODO(payments): confirm the exact production hostnames with the team.
 * India and Thailand are inferred from the domains named when this was
 * scoped. Sri Lanka's UAT host is nurahealth.lk; its production domain
 * didn't exist yet when PayCenter was wired up, so the same suffix is
 * expected to cover it once it does.
 */
export const HOSTNAME_SUFFIXES: Array<[string, CountryCode]> = [
  ['nura.in', 'IN'],
  ['nurathailand.com', 'TH'],
  ['nurahealth.lk', 'LK'],
];

/**
 * Every country string this app accepts from the booking app, mapped to
 * the ISO-2 form the rest of the code uses. The 3-letter entries are
 * legacy — `useBookingSubmit` on the nura-pwa side still sends `IND` —
 * and can go once that side only sends ISO-2. Keys are compared
 * uppercased and trimmed; anything not listed resolves to null rather
 * than being guessed at.
 */
export const LEGACY_COUNTRY_ALIASES: Record<string, CountryCode> = {
  IND: 'IN',
  IN: 'IN',
  TH: 'TH',
  THA: 'TH',
  LK: 'LK',
  LKA: 'LK',
};

/** QA-only escape hatch — deliberately not documented for end users. */
export const COUNTRY_OVERRIDE_PARAM = '__country';
